import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Hide on these route prefixes (in addition to "logged in" check)
const HIDDEN_PREFIXES = [
  '/dashboard', '/create-job', '/edit-job', '/my-jobs',
  '/select-sponsorship', '/checkout', '/applications', '/purchases',
  '/admin', '/candidate/profile', '/favoritos', '/my-applications',
  '/candidate/applications', '/pay/', '/payment-result', '/flyers',
];

function buildSessionId() {
  const existing = sessionStorage.getItem('trabadju_chat_sid');
  if (existing) return existing;
  const sid = 'sess-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  sessionStorage.setItem('trabadju_chat_sid', sid);
  return sid;
}

// Convert plain "**bold**" and `/path` mentions into safe React nodes
function renderRich(text) {
  // Split by **bold** markers
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    // Auto-link internal paths like /register, /jobs/search inside the rest
    const subParts = part.split(/(\/[a-zA-Z][a-zA-Z0-9/_-]*)/g);
    return subParts.map((sp, j) => {
      if (/^\/[a-zA-Z]/.test(sp)) {
        return (
          <a
            key={`${idx}-${j}`}
            href={sp}
            className="text-blue-600 underline hover:text-blue-800"
            data-testid={`chat-link-${sp.replace(/\//g, '-')}`}
          >
            {sp}
          </a>
        );
      }
      return <React.Fragment key={`${idx}-${j}`}>{sp}</React.Fragment>;
    });
  });
}

export default function ChatWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const { i18n } = useTranslation();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant'|'error', text}
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const sessionId = useMemo(buildSessionId, []);

  // Locale-aware UI strings
  const lang = i18n.language || 'pt';
  const T = useMemo(() => {
    const strings = {
      pt: {
        title: 'Tina · Assistente Trabadju',
        subtitle: 'Pergunte-me sobre vagas, candidaturas ou pagamentos',
        placeholder: 'Escreva a sua pergunta...',
        greeting: 'Olá! 👋 Sou a Tina, a assistente virtual do Trabadju. Como posso ajudar?',
        suggestions: [
          'Como publico uma vaga?',
          'Quanto custa um anúncio em destaque?',
          'Como crio o meu perfil de candidato?',
        ],
        error: 'Algo correu mal. Tente novamente.',
        open: 'Abrir chat',
        close: 'Fechar chat',
        send: 'Enviar',
      },
      en: {
        title: 'Tina · Trabadju Assistant',
        subtitle: 'Ask me about jobs, applications or payments',
        placeholder: 'Type your question...',
        greeting: "Hi! 👋 I'm Tina, the Trabadju virtual assistant. How can I help?",
        suggestions: [
          'How do I post a job?',
          'How much is a featured ad?',
          'How do I create a candidate profile?',
        ],
        error: 'Something went wrong. Please try again.',
        open: 'Open chat',
        close: 'Close chat',
        send: 'Send',
      },
      fr: {
        title: 'Tina · Assistant Trabadju',
        subtitle: 'Posez-moi vos questions sur les offres, candidatures et paiements',
        placeholder: 'Tapez votre question...',
        greeting: 'Bonjour ! 👋 Je suis Tina, l’assistante virtuelle de Trabadju. Comment puis-je vous aider ?',
        suggestions: [
          'Comment publier une offre ?',
          'Quel est le prix d’une annonce en vedette ?',
          'Comment créer un profil candidat ?',
        ],
        error: 'Une erreur est survenue. Veuillez réessayer.',
        open: 'Ouvrir le chat',
        close: 'Fermer le chat',
        send: 'Envoyer',
      },
    };
    return strings[lang] || strings.pt;
  }, [lang]);

  // Decide visibility — hide for logged-in users and on private routes
  const isPrivateRoute = HIDDEN_PREFIXES.some((p) =>
    location.pathname === p || location.pathname.startsWith(p + '/') || location.pathname.startsWith(p)
  );
  const hidden = !!user || isPrivateRoute;

  // Scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, busy]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  if (hidden) return null;

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || busy) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/chat/message`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((prev) => [...prev, { role: 'assistant', text: data.reply || '' }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'error', text: T.error }]);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={T.open}
          data-testid="chatbot-launcher"
          className="fixed bottom-6 right-6 z-50 group flex items-center gap-2 bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-4 py-3 rounded-full shadow-2xl ring-1 ring-white/20 transition-all duration-200 hover:scale-105 active:scale-95"
        >
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" />
          </span>
          <MessageCircle className="h-5 w-5" />
          <span className="hidden sm:inline text-sm font-semibold">Tina</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-6 right-4 sm:right-6 z-50 w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-3rem))] flex flex-col rounded-2xl shadow-2xl bg-white border border-gray-200 overflow-hidden"
          data-testid="chatbot-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white px-4 py-3 flex items-start justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className="bg-white/15 rounded-full p-2">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-sm leading-tight truncate">{T.title}</div>
                <div className="text-[11px] text-white/80 truncate">{T.subtitle}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={T.close}
              data-testid="chatbot-close"
              className="rounded-full p-1.5 hover:bg-white/15 transition shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 bg-gray-50 space-y-2.5"
            data-testid="chatbot-messages"
          >
            {/* Greeting bubble */}
            <div className="flex">
              <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%] text-sm text-gray-800 shadow-sm">
                {T.greeting}
              </div>
            </div>

            {/* Suggestion chips (only when no messages yet) */}
            {messages.length === 0 && !busy && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {T.suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => send(s)}
                    data-testid={`chatbot-suggestion-${i}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Conversation */}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : ''}`}>
                <div
                  className={
                    m.role === 'user'
                      ? 'bg-blue-600 text-white rounded-2xl rounded-tr-sm px-3 py-2 max-w-[85%] text-sm shadow-sm whitespace-pre-wrap'
                      : m.role === 'error'
                      ? 'bg-red-50 border border-red-200 text-red-700 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%] text-sm'
                      : 'bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%] text-sm text-gray-800 shadow-sm whitespace-pre-wrap'
                  }
                  data-testid={`chatbot-msg-${m.role}-${i}`}
                >
                  {m.role === 'assistant' ? renderRich(m.text) : m.text}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {busy && (
              <div className="flex">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 bg-white px-3 py-2.5">
            <div className="flex items-end gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={T.placeholder}
                disabled={busy}
                data-testid="chatbot-input"
                className="flex-1 px-3 py-2 text-sm rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:bg-gray-50"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                aria-label={T.send}
                data-testid="chatbot-send"
                className="bg-blue-600 text-white p-2 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <div className="text-[10px] text-gray-400 mt-1.5 text-center">
              Powered by AI · Trabadju.cv
            </div>
          </div>
        </div>
      )}
    </>
  );
}
